import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.js";

const selectors = {
  productGalleryCarousel: ".js-product-gallery-carousel",
  swiperButtonNext: ".swiper-button-next",
  swiperButtonPrev: ".swiper-button-prev",
  swiperPagination: ".swiper-pagination",
  pswpIframe: ".pswp__iframe",
  pswpVideo: ".pswp__video",
  productRight: '.product__right'
};

function isValidYouTubeId(id) {
  return typeof id === "string" && /^[A-Za-z0-9_-]{11}$/.test(id);
}

function isValidVimeoId(id) {
  return typeof id === "string" && /^[0-9]+$/.test(id);
}

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.productGalleryCarousel = null;

    this.swiper = null;
    this.lightbox = null;
    this.gridLightbox = null;

    this._isInitialized = false;
    this._swiperWaitAttempts = 0;
    this._swiperWaitTimer = null;
    this.resizeObserver = null;
  }

  mapMediaIndexToSwiperIndex(mediaIndex) {
    if (typeof mediaIndex !== "number") return mediaIndex;
    if (!this.hasAttribute("data-has-nature-notes")) return mediaIndex;
    return mediaIndex >= 1 ? mediaIndex + 1 : mediaIndex;
  }

  connectedCallback() {
    this.productGalleryCarousel = this.querySelector(
      selectors.productGalleryCarousel
    );
    this.productRight = document.querySelector(selectors.productRight)
    if (!this.productGalleryCarousel) return;
    this.initWhenReady();
  }

  disconnectedCallback() {
    if (this._swiperWaitTimer) {
      clearTimeout(this._swiperWaitTimer);
      this._swiperWaitTimer = null;
    }

    if (this.lightbox) {
      this.lightbox.destroy();
      this.lightbox = null;
    }

    if (this.gridLightbox) {
      this.gridLightbox.destroy();
      this.gridLightbox = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this._isInitialized = false;
    this._swiperWaitAttempts = 0;
  }

  initWhenReady() {
    if (this._isInitialized) return;

    if (typeof window.Swiper !== "function") {
      this._swiperWaitAttempts += 1;
      if (this._swiperWaitAttempts > 100) return;

      this._swiperWaitTimer = setTimeout(() => {
        this._swiperWaitTimer = null;
        if (!this.isConnected) return;
        this.initWhenReady();
      }, 50);
      return;
    }

    this._isInitialized = true;
    this.initProductGallery();
  }

  initProductGallery() {
    if (typeof window.Swiper !== "function") return;

    this.swiper = new window.Swiper(this.productGalleryCarousel, {
      slidesPerView: 1.12,
      watchOverflow: true,
      navigation: {
        nextEl: this.productGalleryCarousel.querySelector(
          selectors.swiperButtonNext
        ),
        prevEl: this.productGalleryCarousel.querySelector(
          selectors.swiperButtonPrev
        ),
      },
      pagination: {
        el: this.productGalleryCarousel.querySelector(
          selectors.swiperPagination
        ),
        type: "fraction",
        formatFractionCurrent: (number) => {
          return String(number).padStart(2, "0");
        },
        formatFractionTotal: (number) => {
          return String(number).padStart(2, "0");
        },
      },
      spaceBetween: 16,
      breakpoints: {
        768: {
          navigation: false,
        },
      }
    });

    const galleryGrid = this.querySelector(".product__gallery-grid");
    const carouselLinks = this.productGalleryCarousel.querySelectorAll("a");

    this.lightbox = new PhotoSwipeLightbox({
      gallery: this.productGalleryCarousel,
      children: "a",
      pswpModule: () => import("./photoswipe.esm.js"),
      counter: false,
      zoom: false,
      closeSVG:
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18.75 5.25L5.25 18.75" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.75 18.75L5.25 5.25" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      arrowPrev: true,
      arrowPrevSVG:
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 19.5L7.5 12L15 4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      arrowNext: true,
      arrowNextSVG:
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 4.5L16.5 12L9 19.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      showHideAnimationType: 'fade',
      initialZoomLevel: "fit"
    });
    this.lightbox.init();

    if (galleryGrid && window.innerWidth >= 768) {
      const gridLinks = galleryGrid.querySelectorAll("a");
      this.gridLightbox = new PhotoSwipeLightbox({
        gallery: galleryGrid,
        children: "a",
        pswpModule: () => import("./photoswipe.esm.js"),
        counter: false,
        zoom: false,
        closeSVG:
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18.75 5.25L5.25 18.75" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.75 18.75L5.25 5.25" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        arrowPrev: true,
        arrowPrevSVG:
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 19.5L7.5 12L15 4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        arrowNext: true,
        arrowNextSVG:
          '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M9 4.5L16.5 12L9 19.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        showHideAnimationType: "fade",
        initialZoomLevel: "fit",
        secondaryZoomLevel: (zoomLevelObject) => {
          return zoomLevelObject.initial * 1.5;
        },
      });
      this.gridLightbox.init();

      this.setupGridLightboxEvents(gridLinks, carouselLinks);
    }

    const children = this.productGalleryCarousel.querySelectorAll("a");
    this.lightbox.on("change", () => {
      const currIndex = this.lightbox.pswp.currIndex;
      const container = this.lightbox.pswp.currSlide.container;
      const currChildren = children[currIndex];
      const overlay = document.querySelector(".pswp__overlay");
      if (overlay) overlay.classList.remove("hidden");
      this.swiper.slideTo(this.mapMediaIndexToSwiperIndex(currIndex));

      if (currChildren && container) {
        const videoType = currChildren.getAttribute("data-video-type");
        let videoElementStr = "";

        if (videoType === "internal") {
          const videoUrl = currChildren.href;
          videoElementStr = `
          <video playsinline="playsinline" controls="controls" class="pswp__video" preload="metadata">
            <source src="${videoUrl}" type="video/mp4">
          </video>
          `;
        }

        if (videoType === "youtube" || videoType === "vimeo") {
          const videoId = currChildren.getAttribute("data-video-id");
          const isValidId =
            videoType === "youtube"
              ? isValidYouTubeId(videoId)
              : isValidVimeoId(videoId);

          if (isValidId) {
            videoElementStr =
              videoType === "youtube"
                ? `
            <div class="pswp__iframe">
              <iframe src="https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=0" frameborder="0" allow="encrypted-media" allowfullscreen></iframe>
            </div>
            `
                : `
            <div class="pswp__iframe">
              <iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allow="encrypted-media" allowfullscreen></iframe>
            </div>
            `;
          } else {
            videoElementStr = "";
          }
        }

        const parentEl = container.parentElement;
        if (parentEl) {
          if (videoElementStr !== "") {
            const parser = new DOMParser();
            const videoElement = parser.parseFromString(
              videoElementStr,
              "text/html"
            );
            parentEl.innerHTML = "";
            parentEl.appendChild(videoElement.body.firstChild);
          } else {
            const iframe = parentEl.querySelector(selectors.pswpIframe);
            const video = parentEl.querySelector(selectors.pswpVideo);
            if (iframe) iframe.remove();
            if (video) video.remove();
          }
        }
      }
    });

    this.lightbox.on("close", () => {
      if (!this.lightbox.pswp) return;
      const currIndex = this.lightbox.pswp.currIndex;
      const targetElement = children[currIndex];
      if (!targetElement) return;

      setTimeout(() => {
        if (this.swiper) {
          this.swiper.slideTo(this.mapMediaIndexToSwiperIndex(currIndex));
        }

        const swiperSlide = targetElement.closest(".swiper-slide");
        if (swiperSlide) {
          swiperSlide.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 100);
    });
  }

  setupGridLightboxEvents(gridLinks, carouselLinks) {
    const children = this.productGalleryCarousel.querySelectorAll("a");

    this.gridLightbox.on("change", () => {
      const currIndex = this.gridLightbox.pswp.currIndex;
      const container = this.gridLightbox.pswp.currSlide.container;
      const currGridLink = gridLinks[currIndex];
      const overlay = document.querySelector(".pswp__overlay");
      if (overlay) overlay.classList.remove("hidden");

      const carouselIndex = Array.from(carouselLinks).findIndex(
        (carouselLink) => {
          const carouselHref = carouselLink.getAttribute("href");
          const gridHref = currGridLink?.getAttribute("href");
          return carouselHref === gridHref;
        }
      );

      if (carouselIndex !== -1 && this.swiper) {
        this.swiper.slideTo(this.mapMediaIndexToSwiperIndex(carouselIndex));
      }

      if (currGridLink && container) {
        const videoType = currGridLink.getAttribute("data-video-type");
        let videoElementStr = "";

        if (videoType === "internal") {
          const videoUrl = currGridLink.href;
          videoElementStr = `
          <video playsinline="playsinline" controls="controls" class="pswp__video" preload="metadata">
            <source src="${videoUrl}" type="video/mp4">
          </video>
          `;
        }

        if (videoType === "youtube" || videoType === "vimeo") {
          const videoId = currGridLink.getAttribute("data-video-id");
          const isValidId =
            videoType === "youtube"
              ? isValidYouTubeId(videoId)
              : isValidVimeoId(videoId);

          if (isValidId) {
            videoElementStr =
              videoType === "youtube"
                ? `
            <div class="pswp__iframe">
              <iframe src="https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=0" frameborder="0" allow="encrypted-media" allowfullscreen></iframe>
            </div>
            `
                : `
            <div class="pswp__iframe">
              <iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allow="encrypted-media" allowfullscreen></iframe>
            </div>
            `;
          } else {
            videoElementStr = "";
          }
        }

        const parentEl = container.parentElement;
        if (parentEl) {
          if (videoElementStr !== "") {
            const parser = new DOMParser();
            const videoElement = parser.parseFromString(
              videoElementStr,
              "text/html"
            );
            parentEl.innerHTML = "";
            parentEl.appendChild(videoElement.body.firstChild);
          } else {
            const iframe = parentEl.querySelector(selectors.pswpIframe);
            const video = parentEl.querySelector(selectors.pswpVideo);
            if (iframe) iframe.remove();
            if (video) video.remove();
          }
        }
      }
    });
  }
}

if (!customElements.get("product-gallery")) {
  customElements.define("product-gallery", ProductGallery);
}
